import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Upload } from '@factorialco/f0-react/icons/app'
import {
  ProjectDocumentsDataCollectionDocument,
  ProjectDocumentsDataCollectionItemFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { DeleteDocumentsModal } from './modals/DeleteDocumentsModal'

interface ProjectDocumentsDataCollectionProps {
  projectId: number
  canManageDocuments: boolean
  onClickUpload: () => void
}

const ProjectDocumentsDataCollection = ({
  projectId,
  canManageDocuments,
  onClickUpload,
}: ProjectDocumentsDataCollectionProps) => {
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([])
  const handleOnClickDelete = () => {
    track(trackingEvents.projectManagement.projectFiles.clickOnDeleteFile)
    setIsOpenModal(true)
  }

  const handleOnCloseModal = () => {
    setIsOpenModal(false)
  }

  const source = useGraphqlDataCollectionSource({
    document: ProjectDocumentsDataCollectionDocument,
    mapData: (data) => {
      return data.projectManagement.projectDocumentsConnection.nodes
    },
    mapVariables: ({ sortings }) => ({
      filterOutDeleted: true,
      projectId,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.projectDocumentsConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totals) => {
      return i18n.tp('project_management.files.datacollection.total_items', { count: totals })
    },
    itemOnClick:
      ({ url }) =>
      () => {
        track(trackingEvents.projectManagement.projectFiles.clickOnDownloadFile)
        window.open(buildUrl(url), '_blank')
      },
    itemActions: ({ id }) => {
      return [
        {
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => {
            setSelectedItemIds([id])
            handleOnClickDelete()
          },
        },
      ]
    },

    primaryActions: () =>
      canManageDocuments
        ? {
            label: i18n.t('project_management.files.datacollection.upload_file'),
            icon: Upload,
            onClick: () => {
              track(trackingEvents.projectManagement.projectFiles.clickOnUploadFile)
              onClickUpload()
            },
          }
        : undefined,
    selectable: (item) => item.id,
    bulkActions: () => {
      return {
        primary: [
          {
            id: 'delete-documents',
            label: i18n.t('common.delete'),
            icon: Delete,
            critical: true,
          },
        ],
      }
    },
    sortings: {
      name: {
        label: i18n.t('project_management.files.datacollection.header.name'),
      },
      createdOn: {
        label: i18n.t('project_management.files.datacollection.header.created_on'),
      },
    },
    defaultSorting: {
      field: 'createdOn',
      order: 'desc',
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('project_management.files.empty_state.title'),
            description: i18n.t('project_management.files.empty_state.description'),
            emoji: '📄',
            actions: [
              {
                label: i18n.t('project_management.files.empty_state.button_text'),
                variant: 'default',
                onClick: () => {
                  onClickUpload()
                },
              },
            ],
          },
        }}
        onBulkAction={(action, selectedItems) => {
          setSelectedItemIds(
            selectedItems.itemsStatus
              .filter((status) => status.checked)
              .map((status) => status.item.id)
          )

          switch (action) {
            case 'delete-documents':
              handleOnClickDelete()
              break
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.files.datacollection.header.name'),
                  render: (item: ProjectDocumentsDataCollectionItemFragment) => ({
                    type: 'text',
                    value: item.name,
                  }),
                  sorting: 'name' as const,
                },
                {
                  label: i18n.t('project_management.files.datacollection.header.uploaded_by'),
                  render: (item: ProjectDocumentsDataCollectionItemFragment) => ({
                    type: 'person',
                    value: {
                      firstName: item.uploader?.firstName ?? '',
                      lastName: item.uploader?.lastName ?? '',
                      src: buildUrl(item.uploader?.access?.avatar),
                    },
                  }),
                },
                {
                  label: i18n.t('project_management.files.datacollection.header.created_on'),
                  render: (item: ProjectDocumentsDataCollectionItemFragment) => ({
                    type: 'date',
                    value: new Date(item.createdOn),
                  }),
                  sorting: 'createdOn' as const,
                },
              ]),
            },
          },
        ]}
      />
      {isOpenModal && (
        <DeleteDocumentsModal
          onCloseModal={handleOnCloseModal}
          ids={selectedItemIds}
          projectId={projectId}
        />
      )}
    </>
  )
}

export default ProjectDocumentsDataCollection
